import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  Box, Typography, Button, IconButton, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, Tooltip, CircularProgress,
  Snackbar, Checkbox, ListItemText,
  Accordion, AccordionSummary, AccordionDetails
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DeleteIcon from "@material-ui/icons/Delete";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import ImageIcon from "@material-ui/icons/Image";
import VideocamIcon from "@material-ui/icons/Videocam";
import { AuthContext } from "../../context/Auth/AuthContext";
import AiVideoModal from "../AiVideo/AiVideoModal";
import PeopleIcon from "@material-ui/icons/People";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import SyncIcon from "@material-ui/icons/Sync";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import moment from "moment-timezone";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import Paper from "@material-ui/core/Paper";
import FileManagerSelectorModal from "../../components/FileManagerSelectorModal";
import AiContentFactory from "../../components/AiContentFactory";
import AiPromptViewer from "../../components/AiPromptViewer";
import PageHelpGuide from "../../components/PageHelpGuide";
import StandardTabs from "../../components/StandardTabs";
import { COMMUNITY_HELP_DATA } from "../../constants/pageHelp/communityHelp";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  cyanButton: {
    ...getStandardButtonStyles(theme).applyButton,
  },
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 20px ${theme.palette.primary.main}80 !important` 
      : `0 0 16px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden !important",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%",
    borderRadius: 16,
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingTop: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  root: {
    padding: theme.spacing(3),
    maxWidth: 1400,
    margin: "0 auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    fontSize: "1.4rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  exclusiveBadge: {
    backgroundColor: `${theme.palette.primary.main}26`,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}80`,
    borderRadius: 20,
    padding: "2px 12px",
    fontSize: "0.72rem",
    fontWeight: 700,
    marginLeft: theme.spacing(1),
  },
  scheduleCard: {
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}`,
    boxShadow: theme.palette.type === "dark" ? `0 0 10px ${theme.palette.primary.main}26` : "0 2px 8px rgba(0,0,0,0.06)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 12,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
    transition: "box-shadow 0.2s, border-color 0.2s",
    "&:hover": { 
      boxShadow: `0 0 16px ${theme.palette.primary.main}66`,
      borderColor: theme.palette.primary.main,
    },
  },
  inactiveCard: {
    opacity: 0.9,
    borderStyle: "solid",
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  fileInputRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  sentBadge: {
    backgroundColor: `${theme.palette.primary.main}26`,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}80`,
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: "0.75rem",
    fontWeight: 700,
    display: "inline-block",
    marginTop: 4
  },
  weekHeader: {
    backgroundColor: `${theme.palette.primary.main}1A`,
    borderBottom: `1px solid ${theme.palette.primary.main}4D`,
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: "transparent",
  }
}));

export default function CommunityScheduler() {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { get: getCompanySetting } = useCompanySettings();
  const [companyTimezone, setCompanyTimezone] = useState("America/Lima");

  const [connections, setConnections] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("all");
  const [previewMedia, setPreviewMedia] = useState(null);
  const [viewSchedule, setViewSchedule] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [restrictedGroups, setRestrictedGroups] = useState({});
  const [mentionAll, setMentionAll] = useState(false);
  const [aiVideoOpen, setAiVideoOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectorModalOpen, setSelectorModalOpen] = useState(false);
  const [aiFactoryOpen, setAiFactoryOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isDarkMode = theme.palette.type === "dark";
  const isAdmin = Boolean(user?.super || (user?.profile === "admin" && user?.companyId === 1));

  const SCHEDULER_TAB_DEFINITIONS = [
    {
      key: "manual",
      labelKey: "statusScheduler.tabs.manual",
      defaultLabel: "Publicaciones Manuales",
      color: "#00E676",
      Icon: EditIcon,
    },
    {
      key: "aiImage",
      labelKey: "statusScheduler.tabs.aiImage",
      defaultLabel: "Publicaciones con IA (Imagen)",
      color: "#00E5FF",
      Icon: ImageIcon,
    },
    {
      key: "aiVideo",
      labelKey: "statusScheduler.tabs.aiVideo",
      defaultLabel: "Publicaciones con IA (Video)",
      color: "#E040FB",
      Icon: VideocamIcon,
      isBeta: true,
    },
  ];

  const handleTabChange = (e, newValue) => {
    if (newValue === 2 && !isAdmin) {
      toastError(
        i18n.t("statusScheduler.tabs.aiVideoBetaRestricted") ||
        "Esta funcionalidad con IA de Video se encuentra en fase Beta exclusiva para el administrador."
      );
      return;
    }
    setTabValue(newValue);
  };

  const renderSchedulerTabLabel = (index) => {
    const tabDef = SCHEDULER_TAB_DEFINITIONS[index];
    const isActive = tabValue === index;
    const isVideoRestricted = tabDef.isBeta && !isAdmin;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          color: isActive
            ? (isDarkMode ? "#ffffff" : "#111827")
            : (isDarkMode ? "rgba(255, 255, 255, 0.65)" : "rgba(0, 0, 0, 0.6)"),
          transition: "all 0.25s ease",
          userSelect: "none",
          opacity: isVideoRestricted ? 0.75 : 1,
        }}
      >
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 12px" }}>
          <tabDef.Icon
            style={{
              marginRight: 8,
              color: tabDef.color,
              filter: isActive ? `drop-shadow(0 0 3px ${tabDef.color})` : "none",
              opacity: isActive ? 1 : 0.65,
              fontSize: "1.35rem",
              transition: "all 0.25s ease",
            }}
          />
          <span style={{
            fontWeight: isActive ? 900 : 600,
            lineHeight: 1, // Previene expansión vertical
            textShadow: isActive ? (isDarkMode ? "0 0 3px rgba(255, 255, 255, 0.4)" : "none") : "none",
            letterSpacing: "0.2px",
            whiteSpace: "nowrap",
          }}>
            {i18n.t(tabDef.labelKey) || tabDef.defaultLabel}
          </span>
          {tabDef.isBeta && (
            <span
              style={{
                marginLeft: 8,
                padding: "2px 7px",
                borderRadius: 10,
                fontSize: "0.65rem",
                fontWeight: 800,
                letterSpacing: "0.5px",
                background: "linear-gradient(135deg, #FF6D00 0%, #FF1744 100%)",
                color: "#ffffff",
                boxShadow: "0 0 5px rgba(255, 109, 0, 0.4)",
                textTransform: "uppercase",
                lineHeight: 1.2,
              }}
            >
              Beta
            </span>
          )}
        </div>
        <div style={{
          height: 3,
          minHeight: 3,
          flexShrink: 0,
          width: "100%",
          backgroundColor: isActive ? tabDef.color : "transparent",
          boxShadow: isActive ? `0 0 6px ${tabDef.color}` : "none",
          borderRadius: "3px 3px 0 0",
          transition: "all 0.25s ease",
          marginBottom: isActive ? "2px" : "0px", // Evita recorte en el desbordamiento
        }} />
      </div>
    );
  };
  const [aiQuantity, setAiQuantity] = useState(1);
  const [aiEditSchedule, setAiEditSchedule] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [syncingGroups, setSyncingGroups] = useState(false);
  const [form, setForm] = useState({
    whatsappId: "",
    groupIds: [],
    scheduledAt: moment().tz("America/Lima").format("YYYY-MM-DDTHH:mm"),
    mediaType: "text",
    caption: "",
    mediaUrl: "",
    isActive: true,
    mentionAll: false,
    sendInterval: 15,
    aiGeneration: false,
    aiPrompt: ""
  });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const [importBulkOpen, setImportBulkOpen] = useState(false);
  const [importWeekSchedules, setImportWeekSchedules] = useState([]);
  const [importBulkForm, setImportBulkForm] = useState({ whatsappId: "", groupIds: [] });
  const [importingBulk, setImportingBulk] = useState(false);
  const [importSummary, setImportSummary] = useState(null); // { total, byWeek, destLabel, clampedCount }
  const [importDestWeekStart, setImportDestWeekStart] = useState(null); // moment() de inicio semana destino (null = mes completo)
  const fileInputRef = React.useRef(null);
  const bulkFileInputRef = React.useRef(null);
  const now = moment().tz(companyTimezone);
  const [selectedYear, setSelectedYear] = useState(now.year());
  const [selectedMonth, setSelectedMonth] = useState(now.month()); // 0-indexed

  useEffect(() => {
    getCompanySetting({ column: "timezone" }).then(res => {
      if (res && res.timezone) {
        setCompanyTimezone(res.timezone);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/whatsapp?session=0").then(({ data }) => {
      const baileys = (data || []).filter(
        w => !w.channel || w.channel === "whatsapp"
      );
      setConnections(baileys);
      if (baileys.length > 0) setSelectedWhatsapp("all");
    });

    const fetchGroups = async () => {
      try {
        let allGroups = [];
        let pageNumber = 1;
        let hasMore = true;
        
        while (hasMore) {
          const { data } = await api.get("/contacts", {
            params: { isGroup: "true", pageNumber }
          });
          allGroups = [...allGroups, ...data.contacts];
          hasMore = data.hasMore;
          pageNumber++;
        }
        setAvailableGroups(allGroups.filter(g => g.number && g.active !== false));
      } catch (err) {
        console.error("Error fetching groups", err);
      }
    };
    fetchGroups();
  }, []);

  const fetchGroupsManual = async () => {
    try {
      let allGroups = [];
      let pageNumber = 1;
      let hasMore = true;
      
      while (hasMore) {
        const { data } = await api.get("/contacts", {
          params: { isGroup: "true", pageNumber }
        });
        allGroups = [...allGroups, ...data.contacts];
        hasMore = data.hasMore;
        pageNumber++;
      }
      setAvailableGroups(allGroups.filter(g => g.number && g.active !== false));
    } catch (err) {
      console.error("Error fetching groups", err);
    }
  };

  const handleSyncGroups = async () => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") return;
    setSyncingGroups(true);
    try {
      const { data } = await api.post(`/whatsapp/${selectedWhatsapp}/sync-groups`);
      const hiddenCount = data.deactivatedCount || 0;
      const successMsg = i18n.t("statusScheduler.syncCompleteHidden", { count: hiddenCount });
      showSnack(successMsg, "success");
      await fetchGroupsManual(); // Recargar la lista de grupos tras sincronizar
      await loadMetadata(); // Recargar los metadatos para descubrir comunidades
    } catch (err) {
      console.error("Error al sincronizar grupos", err);
      showSnack(i18n.t("statusScheduler.syncError"), "error");
    } finally {
      setSyncingGroups(false);
    }
  };

  const handleVideoGenerated = async (videoUrl, prompt) => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") return;
    try {
      const payload = {
        groupIds: [],
        scheduledAt: new Date().toISOString(),
        mediaType: "video",
        mediaUrl: videoUrl,
        caption: "",
        aiGeneration: true,
        aiPrompt: prompt || "Video Generado",
        mentionAll: false,
        sendInterval: 15,
        isActive: false, // Guardado como borrador
        isCommunity: true,
        whatsappId: selectedWhatsapp
      };
      await api.post("/group-schedule", payload);
      loadSchedules();
    } catch (err) {
      console.error("Error creating video draft", err);
    }
  };

  const loadMetadata = useCallback(async () => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      setRestrictedGroups({});
      return;
    }
    try {
      const { data } = await api.get(`/group-schedule/metadata/${selectedWhatsapp}`);
      setRestrictedGroups(data);
    } catch (err) {
      console.error("Error loading group metadata", err);
    }
  }, [selectedWhatsapp]);

  const loadSchedules = useCallback(async () => {
    if (!selectedWhatsapp) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/group-schedule?whatsappId=${selectedWhatsapp}&isCommunity=true`);
      const sortedData = data.sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
      setSchedules(sortedData);
    } catch (err) {
      showSnack(i18n.t("statusScheduler.errorLoad") || "Error al cargar las programaciones", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedWhatsapp]);

  useEffect(() => {
    if (selectedWhatsapp) {
      loadSchedules();
      loadMetadata();
    }
  }, [selectedWhatsapp, loadSchedules, loadMetadata]);

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const onCompanyGroupSchedule = (data) => {
      if (data.schedule && !data.schedule.isCommunity) return; // Ignore non-community updates
      
      if (data.action === "update" || data.action === "create") {
        setSchedules((prev) => {
          const idx = prev.findIndex(s => s.id === data.schedule.id);
          let newSchedules = [...prev];
          if (idx !== -1) {
            newSchedules[idx] = data.schedule;
          } else {
            newSchedules.push(data.schedule);
          }
          return newSchedules.sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
        });
      }

      if (data.action === "delete") {
        setSchedules((prev) => prev.filter(s => s.id !== +data.scheduleId));
      }
      
      if (data.action === "clone") {
        loadSchedules();
      }
    };

    socket.on(`company-${user.companyId}-groupSchedule`, onCompanyGroupSchedule);

    return () => {
      socket.off(`company-${user.companyId}-groupSchedule`, onCompanyGroupSchedule);
    };
  }, [socket, user?.companyId, loadSchedules]);

  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  const getErrorMessage = (err, defaultMsgKey = "statusScheduler.errorSave") => {
    const errorMsg = err?.response?.data?.error;
    if (errorMsg && i18n.exists(`backendErrors.${errorMsg}`)) {
      return i18n.t(`backendErrors.${errorMsg}`);
    }
    return errorMsg || err?.message || i18n.t(defaultMsgKey) || "Error al guardar";
  };

  const handleExportCard = (s) => {
    const data = { ...s };
    delete data.id; delete data.companyId; delete data.createdAt; delete data.updatedAt;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `comunidad_tarjeta_${moment().format("YYYYMMDD_HHmmss")}.json`;
    a.click();
  };

  const handleImportCard = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (Array.isArray(imported)) throw new Error("El archivo contiene mÃºltiples tarjetas. Usa el importador semanal.");
        setEditingId(null);
        setForm({
          whatsappId: imported.whatsappId || (selectedWhatsapp === "all" ? "" : selectedWhatsapp),
          groupIds: imported.groupIds || [],
          scheduledAt: moment().tz(companyTimezone).add(5, 'minutes').format("YYYY-MM-DDTHH:mm"),
          mediaType: imported.mediaType || "text",
          caption: imported.caption || "",
          mediaUrl: "", 
          isActive: imported.isActive ?? true,
          mentionAll: imported.mentionAll || false,
          sendInterval: imported.sendInterval || 15,
          aiGeneration: imported.aiGeneration || false,
          aiPrompt: imported.aiPrompt || ""
        });
        setModalOpen(true);
      } catch (err) {
        showSnack("Error al importar JSON: " + err.message, "error");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleExportWeek = (weekSchedules, weekName) => {
    const data = weekSchedules.map(s => {
      const copy = { ...s };
      delete copy.id; delete copy.createdAt; delete copy.updatedAt; delete copy.status;
      return copy;
    });
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `comunidades_${weekName.replace(/\s+/g, '_')}.json`;
    a.click();
  };

  const handleImportBulkFile = (e, destWeekStart = null) => {
    const file = e.target.files[0];
    const inputTarget = e.target;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (!Array.isArray(imported)) throw new Error("El archivo no es una exportación válida (debe ser un arreglo de tarjetas).");
        if (imported.length === 0) throw new Error("El archivo está vacío.");

        // ─── Validar que el destino no sea el pasado ───
        const nowTz = moment().tz(companyTimezone);
        if (destWeekStart) {
          // Importar en semana específica
          const weekEnd = destWeekStart.clone().endOf('isoWeek');
          if (weekEnd.isBefore(nowTz, 'day')) {
            showSnack("⛔ No puedes importar en una semana que ya finalizó.", "error");
            if (inputTarget) inputTarget.value = "";
            return;
          }
        } else {
          // Importar en mes/global
          const destMonthEnd = moment().tz(companyTimezone).year(selectedYear).month(selectedMonth).endOf('month');
          if (destMonthEnd.isBefore(nowTz, 'day')) {
            showSnack("⛔ No puedes importar en un mes que ya finalizó.", "error");
            if (inputTarget) inputTarget.value = "";
            return;
          }
        }

        // ─── Motor de análisis ───
        const sorted = [...imported].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
        const baseOriginal = moment(sorted[0].scheduledAt).tz(companyTimezone);
        const baseDestino = destWeekStart
          ? (destWeekStart.isSame(nowTz, 'day') || destWeekStart.isBefore(nowTz) ? nowTz.clone() : destWeekStart.clone().startOf('isoWeek'))
          : nowTz.clone();

        // Calcular tarjetas proyectadas con Delta Mapping
        let clampedCount = 0;
        let lastValidDate = nowTz.clone().add(4, 'minutes');
        let currentPushOffset = 0;
        const baseOrigDay = baseOriginal.clone().startOf('day');

        const projected = sorted.map(s => {
          const origDate = moment(s.scheduledAt).tz(companyTimezone);
          const origDay = origDate.clone().startOf('day');
          const deltaDays = origDay.diff(baseOrigDay, 'days');
          const deltaMinutes = origDate.hours() * 60 + origDate.minutes();
          
          let mappedDate = baseDestino.clone().startOf('day').add(deltaDays, 'days').add(deltaMinutes, 'minutes');

          // Aplicamos el desfase actual a todas para no romper el espaciado interno del bloque
          mappedDate.add(currentPushOffset, 'milliseconds');

          if (mappedDate.isBefore(lastValidDate)) {
             clampedCount++;
             const extraPush = lastValidDate.diff(mappedDate);
             currentPushOffset += extraPush;
             mappedDate.add(extraPush, 'milliseconds');
          }

          lastValidDate = mappedDate.clone().add(1, 'minute');
          return { ...s, _projectedDate: mappedDate };
        });

        // Calcular resumen por semana destino
        const byWeek = {};
        projected.forEach(s => {
          const weekLabel = `${i18n.t("statusScheduler.weekWord")} del ${s._projectedDate.clone().startOf('isoWeek').format('DD MMM')} al ${s._projectedDate.clone().endOf('isoWeek').format('DD MMM')}`;
          if (!byWeek[weekLabel]) byWeek[weekLabel] = 0;
          byWeek[weekLabel]++;
        });

        // Origen del archivo
        const isSameCompany = sorted.every(s => s.companyId === user?.companyId);
        const origFirstDate = moment(sorted[0].scheduledAt).tz(companyTimezone);
        const origLastDate = moment(sorted[sorted.length - 1].scheduledAt).tz(companyTimezone);
        const originLabel = `${origFirstDate.format('DD MMM YYYY')} — ${origLastDate.format('DD MMM YYYY')}`;

        const destLabel = destWeekStart
          ? `${i18n.t("statusScheduler.weekWord")} del ${destWeekStart.clone().startOf('isoWeek').format('DD MMM')} al ${destWeekStart.clone().endOf('isoWeek').format('DD MMM YYYY')}`
          : `${moment().month(selectedMonth).format('MMMM')} ${selectedYear}`;

        setImportSummary({
          total: sorted.length,
          byWeek,
          destLabel,
          originLabel,
          clampedCount,
          projected,
          isSameCompany
        });
        setImportDestWeekStart(destWeekStart);
        setImportWeekSchedules(sorted);
        setImportBulkForm({ whatsappId: selectedWhatsapp === "all" ? "" : selectedWhatsapp, groupIds: [] });
        setImportBulkOpen(true);
      } catch (err) {
        showSnack("Error: " + err.message, "error");
      }
    };
    reader.readAsText(file);
    if (inputTarget) inputTarget.value = "";
  };

  const processBulkImport = async () => {
    if (!importBulkForm.whatsappId) return showSnack("Selecciona una conexión", "error");
    if (importBulkForm.groupIds.length === 0) return showSnack("Selecciona al menos una comunidad", "error");
    if (!importSummary || !importSummary.projected) return showSnack("No hay datos para importar.", "error");

    setImportingBulk(true);
    let successCount = 0;
    for (let s of importSummary.projected) {
      try {
        const { _projectedDate, id, companyId, createdAt, updatedAt, ...rest } = s;
        const payload = {
          ...rest,
          whatsappId: importBulkForm.whatsappId,
          groupIds: importBulkForm.groupIds,
          mediaUrl: importSummary.isSameCompany ? (s.mediaUrl || "") : "",
          isActive: false,
          isCommunity: true,
          status: "PENDING",
          scheduledAt: _projectedDate.toISOString()
        };
        await api.post("/group-schedule", payload);
        successCount++;
      } catch (e) {
        console.error("Error importing bulk schedule", e);
      }
    }
    setImportingBulk(false);
    setImportBulkOpen(false);
    setImportSummary(null);
    showSnack(`✅ Se importaron ${successCount} de ${importSummary.projected.length} tarjetas exitosamente. Todas están Inactivas.`);
    loadSchedules();
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({
      whatsappId: selectedWhatsapp === "all" ? "" : selectedWhatsapp,
      groupIds: [],
      scheduledAt: moment().tz(companyTimezone).format("YYYY-MM-DDTHH:mm"),
      mediaType: "text",
      caption: "",
      mediaUrl: "",
      isActive: true,
      mentionAll: false,
      sendInterval: 15,
      aiGeneration: false,
      aiPrompt: ""
    });
    setModalOpen(true);
  };

  const openEdit = (s) => {
    if (s.aiGeneration) {
      setAiEditSchedule(s);
      setAiFactoryOpen(true);
      return;
    }
    
    setEditingId(s.id);
    setForm({
      whatsappId: s.whatsappId,
      groupIds: s.groupIds || [],
      scheduledAt: moment(s.scheduledAt).tz(companyTimezone).format("YYYY-MM-DDTHH:mm"),
      mediaType: s.mediaType,
      caption: s.caption || "",
      mediaUrl: s.mediaUrl || "",
      isActive: s.isActive,
      mentionAll: s.mentionAll || false,
      sendInterval: s.sendInterval || 15,
      aiGeneration: s.aiGeneration || false,
      aiPrompt: s.aiPrompt || "",
      status: s.status
    });
    setModalOpen(true);
  };

  const openClone = (s) => {
    setEditingId(null);
    setForm({
      groupIds: s.groupIds || [],
      scheduledAt: moment().tz(companyTimezone).add(5, 'minutes').format("YYYY-MM-DDTHH:mm"),
      mediaType: s.mediaType,
      caption: s.caption || "",
      mediaUrl: s.mediaUrl || "",
      isActive: s.isActive,
      mentionAll: s.mentionAll || false,
      sendInterval: s.sendInterval || 15,
      aiGeneration: s.aiGeneration || false,
      aiPrompt: s.aiPrompt || ""
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (form.groupIds.length === 0) throw new Error(i18n.t("statusScheduler.errorSelectCommunity") || "Debe seleccionar al menos una comunidad.");
      if (!form.scheduledAt) throw new Error(i18n.t("statusScheduler.errorSelectDate") || "Debe seleccionar fecha y hora.");
      
      // Validado basado en la zona horaria de la empresa (mínimo 3 minutos en el futuro).
      if (moment.tz(form.scheduledAt, companyTimezone).isBefore(moment().tz(companyTimezone).add(3, 'minutes'))) {
        throw new Error("La fecha y hora programada debe ser al menos 3 minutos en el futuro según la zona horaria de la empresa.");
      }

      const payload = {
        ...form,
        whatsappId: form.whatsappId || selectedWhatsapp,
        isCommunity: true,
        scheduledAt: moment.tz(form.scheduledAt, companyTimezone).toISOString()
      };
      
      if (editingId) {
        await api.put(`/group-schedule/${editingId}`, payload);
      } else {
        await api.post("/group-schedule", payload);
      }
      setModalOpen(false);
      showSnack(i18n.t("statusScheduler.saved") || "Programación guardada exitosamente");
      loadSchedules();
    } catch (err) {
      showSnack(getErrorMessage(err), "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(i18n.t("statusScheduler.confirmDelete") || "¿Seguro que deseas eliminar esta programación?")) return;
    try {
      await api.delete(`/group-schedule/${id}`);
      showSnack(i18n.t("statusScheduler.deleted") || "Eliminada correctamente");
      loadSchedules();
    } catch (err) {
      showSnack(getErrorMessage(err, "statusScheduler.errorDelete"), "error");
    }
  };

  const handleToggleActive = async (s) => {
    try {
      await api.put(`/group-schedule/${s.id}`, { isActive: !s.isActive });
      loadSchedules();
    } catch (err) {
      showSnack(getErrorMessage(err, "statusScheduler.errorSave"), "error");
    }
  };

  const MediaIcon = ({ type }) => {
    if (type === "image") return <ImageIcon fontSize="small" style={{ color: "#43a047" }} />;
    if (type === "video") return <VideocamIcon fontSize="small" style={{ color: "#e53935" }} />;
    if (type === "document") return <InsertDriveFileIcon fontSize="small" style={{ color: "#fb8c00" }} />;
    if (type === "audio") return <AudiotrackIcon fontSize="small" style={{ color: "#8e24aa" }} />;
    return <TextFieldsIcon fontSize="small" style={{ color: "#1976d2" }} />;
  };

  // Filter groups: must be a community AND the bot must be an administrator (not restricted)
  const activeAdminCommunities = availableGroups.filter((group) => {
    const meta = restrictedGroups[group.id];
    return meta?.isCommunity && !meta?.isRestricted;
  });

  const hasActiveCommunities = activeAdminCommunities.length > 0;

  const filteredSchedules = schedules.filter(s => {
    const isVideo = s.mediaType === "video";
    if (tabValue === 0) return !s.aiGeneration;
    if (tabValue === 1) return s.aiGeneration && !isVideo;
    if (tabValue === 2) return s.aiGeneration && isVideo;
    return true;
  });

  // getBase simple para localhost
  const getBase = () => process.env.REACT_APP_BACKEND_URL || '';
  const getMediaUrl = (relPath, companyId) => {
    const base = getBase();
    if (!relPath) return '';
    if (relPath.startsWith('http')) return relPath;
    
    if (relPath.includes('public/')) {
        return `${base}/public/${relPath.split('public/')[1]}`;
    }
    
    if (relPath.startsWith('ai_img_')) return `${base}/public/company${companyId || 1}/campaigns/${relPath.split('/').pop()}`;
    if (relPath.startsWith('/campaigns/')) return `${base}/public/company${companyId || 1}${relPath}`;
    
    return `${base}/public/company${companyId || 1}/files/${relPath.split('/').pop()}`;
  };


  // ── Años disponibles: los que tienen tarjetas + año actual + 2 futuros
  const yearsWithCards = [...new Set(schedules.map(s => moment(s.scheduledAt).tz(companyTimezone).year()))];
  const nowYear = moment().tz(companyTimezone).year();
  const availableYears = [...new Set([...yearsWithCards, nowYear, nowYear + 1, nowYear + 2])].sort((a, b) => a - b);
  const connectionSelected = selectedWhatsapp && selectedWhatsapp !== "all";

  // ── Filtrar por año y mes seleccionados
  const schedulesForMonth = filteredSchedules.filter(s => {
    const m = moment(s.scheduledAt).tz(companyTimezone);
    return m.year() === selectedYear && m.month() === selectedMonth;
  });

  // ── Agrupar en semanas del mes (Semana 1 = días 1-7, Semana 2 = días 8-14, etc.)
  const groupedByWeek = {};
  const monthStart = moment().tz(companyTimezone).year(selectedYear).month(selectedMonth).startOf('month');
  const monthEnd = monthStart.clone().endOf('month');

  // Generar bloques de semana del mes
  let weekNum = 1;
  let weekCursor = monthStart.clone().startOf('isoWeek');
  // Si la primera isoWeek empieza antes del mes, aun así la incluimos si tiene días del mes
  const weekBlocks = [];
  while (weekCursor.isSameOrBefore(monthEnd, 'day')) {
    const weekStart = weekCursor.clone();
    const weekEnd = weekCursor.clone().endOf('isoWeek');
    // Solo incluir semanas que tengan al menos 1 día dentro del mes
    if (weekEnd.isSameOrAfter(monthStart, 'day') && weekStart.isSameOrBefore(monthEnd, 'day')) {
      const effectiveStart = moment.max(weekStart, monthStart);
      const effectiveEnd = moment.min(weekEnd, monthEnd);
      weekBlocks.push({
        key: `sem-${weekNum}`,
        label: `${i18n.t("statusScheduler.weekWord")} ${weekNum} (${effectiveStart.format('DD MMM')} — ${effectiveEnd.format('DD MMM')})`,
        start: weekStart.clone(), // inicio real de la isoWeek (para imports)
        effectiveStart: effectiveStart.clone(),
        effectiveEnd: effectiveEnd.clone(),
        weekNum
      });
      weekNum++;
    }
    weekCursor.add(1, 'week');
  }

  // Asignar tarjetas a cada bloque de semana
  weekBlocks.forEach(block => {
    block.items = schedulesForMonth.filter(s => {
      const d = moment(s.scheduledAt).tz(companyTimezone);
      return d.isSameOrAfter(block.effectiveStart, 'day') && d.isSameOrBefore(block.effectiveEnd, 'day');
    });
  });

  return (
    <MainContainer>
      <Paper className={classes.mainPaper} variant="outlined">
        <MainHeader>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <PageHelpGuide
              helpData={COMMUNITY_HELP_DATA}
              tooltipText="Guía y Ayuda de Comunidades (Haz clic para ver)"
            />
            <Title>
              {i18n.t("statusScheduler.communitySchedulerTitle") || "Publicar en Comunidad"}
              <span className={classes.exclusiveBadge}>{i18n.t("statusScheduler.onlyBaileys") || "Solo Baileys"}</span>
            </Title>
          </div>
          <MainHeaderButtonsWrapper>
            {/* Action buttons */}
          </MainHeaderButtonsWrapper>
        </MainHeader>
        
        <Box style={{ padding: "16px 16px 0" }}>
          <Alert severity="info" variant="outlined" style={{ borderRadius: 8 }}>
            <span dangerouslySetInnerHTML={{ __html: i18n.t("statusScheduler.alertConfigRequiredCommunities") }}></span>
          </Alert>
        </Box>

        <Box display="flex" alignItems="center" px={2} mt={2} mb={1} style={{ gap: 16 }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 250 }}>
            <InputLabel>{i18n.t("statusScheduler.selectConnection")}</InputLabel>
            <Select
              value={selectedWhatsapp}
              onChange={e => setSelectedWhatsapp(e.target.value)}
              label={i18n.t("statusScheduler.selectConnection")}
            >
              <MenuItem value="all">{i18n.t("statusScheduler.allConnections") || "Todas las conexiones"}</MenuItem>
              {connections.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title={i18n.t("statusScheduler.syncTooltip") || "Sincronizar y limpiar grupos fantasma"}>
            <span>
              <Button
                onClick={handleSyncGroups}
                disabled={syncingGroups || !selectedWhatsapp}
                startIcon={syncingGroups ? <CircularProgress size={20} /> : <SyncIcon />}
                className={classes.secondaryButton}
                style={{ height: 36 }}
              >
                {i18n.t("statusScheduler.syncGroups") || "Sincronizar"}
              </Button>
            </span>
          </Tooltip>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} px={2} style={{ borderBottom: "1px solid rgba(128,128,128,0.2)", flexWrap: "wrap", gap: 16, paddingBottom: 12 }}>
          <StandardTabs
            value={tabValue}
            onChange={handleTabChange}
            items={[
              {
                value: 0,
                label: i18n.t("statusScheduler.tabs.manual") || "Publicaciones Manuales",
                icon: <EditIcon style={{ color: "#00E676" }} />
              },
              {
                value: 1,
                label: i18n.t("statusScheduler.tabs.aiImage") || "Publicaciones con IA (Imagen)",
                icon: <ImageIcon style={{ color: "#00E5FF" }} />
              },
              {
                value: 2,
                label: "Publicaciones con IA (Video)",
                badge: "BETA",
                icon: <VideocamIcon style={{ color: "#E040FB" }} />
              }
            ]}
          />

          <Button
            variant="contained"
            className={classes.applyButton}
            startIcon={<AddIcon />}
            onClick={() => {
              if (tabValue === 1) {
                setAiFactoryOpen(true);
              } else if (tabValue === 2) {
                setAiVideoOpen(true);
              } else {
                openAdd();
              }
            }}
            disabled={!selectedWhatsapp || selectedWhatsapp === "all" || !hasActiveCommunities}
            id="btn-add-community-schedule"
          >
            {tabValue === 1 || tabValue === 2 ? i18n.t("statusScheduler.newAiPost") : (i18n.t("statusScheduler.communitySchedulerAdd") || "Agregar Programación")}
          </Button>
        </Box>

        {/* ── Selectores Año / Mes ── */}
        <Box display="flex" alignItems="center" px={2} pb={1} style={{ gap: 12 }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
          <InputLabel>{i18n.t("statusScheduler.year")}</InputLabel>
          <Select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} label={i18n.t("statusScheduler.year")}>
            {availableYears.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>{i18n.t("statusScheduler.month")}</InputLabel>
          <Select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} label={i18n.t("statusScheduler.month")}>
            {Array.from({ length: 12 }, (_, i) => (
              <MenuItem key={i} value={i}>{moment().month(i).format('MMMM')}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {connectionSelected && (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Tooltip title={i18n.t("statusScheduler.exportMonth")}>
              <span>
                <IconButton size="small" onClick={() => handleExportWeek(schedulesForMonth, `${moment().month(selectedMonth).format('MMMM')}_${selectedYear}`)} disabled={schedulesForMonth.length === 0} style={{ color: schedulesForMonth.length === 0 ? "#555" : "#26D0CE" }}>
                  <GetAppIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t("statusScheduler.importMonth")}>
              <span>
                <IconButton size="small" onClick={() => bulkFileInputRef.current && bulkFileInputRef.current.click()} style={{ color: "#26D0CE" }}>
                  <PublishIcon fontSize="small" />
                </IconButton>
                <input type="file" ref={bulkFileInputRef} style={{ display: 'none' }} accept=".json" onChange={e => handleImportBulkFile(e)} />
              </span>
            </Tooltip>
          </Box>
        )}
        
        <Typography variant="body2" style={{ color: "#cccccc" }}>
          {connectionSelected 
            ? i18n.t("statusScheduler.cardsInMonth", { count: schedulesForMonth.length, month: moment().month(selectedMonth).format('MMMM'), year: selectedYear }) 
            : i18n.t("statusScheduler.selectConnectionToView")}
        </Typography>
      </Box>

        <Box style={{ padding: "0 16px" }}>
          {!hasActiveCommunities && selectedWhatsapp && (
            <Alert severity="warning" variant="outlined" style={{ borderRadius: 8, marginBottom: 16 }}>
              {i18n.t("statusScheduler.noActiveCommunities")}
            </Alert>
          )}
          <Alert severity="info" variant="outlined" style={{ borderRadius: 8, marginBottom: 16 }}>
            <span dangerouslySetInnerHTML={{ __html: i18n.t("statusScheduler.communityToolInfo") }} />
          </Alert>
          <Alert severity="success" variant="outlined" style={{ borderRadius: 8, marginBottom: 16 }}>
            <span dangerouslySetInnerHTML={{ __html: i18n.t("statusScheduler.aiDownloadInfo") }} />
          </Alert>
        </Box>
          
          <div className={classes.scrollArea}>
        {!connectionSelected ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={6} style={{ gap: 12, color: "#888" }}>
          <Typography variant="h6" style={{ color: "#555" }}>{i18n.t("statusScheduler.selectConnectionTitleWp")}</Typography>
          <Typography variant="body2">{i18n.t("statusScheduler.selectConnectionDescComms")}</Typography>
        </Box>
      ) : loading ? (
          <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
        ) : (
          <Box style={{ padding: 16 }}>
            {schedulesForMonth.length === 0 && weekBlocks.length > 0 && (
              <Typography style={{ textAlign: "center", color: "#888", marginTop: 20 }}>
                {i18n.t("statusScheduler.emptyMonthCards", { month: moment().month(selectedMonth).format('MMMM'), year: selectedYear })}
              </Typography>
            )}
            {weekBlocks.length === 0 && (
              <Typography style={{ textAlign: "center", color: "#888", marginTop: 20 }}>
                {i18n.t("statusScheduler.emptyMonth", { month: moment().month(selectedMonth).format('MMMM'), year: selectedYear })}
              </Typography>
            )}
            {weekBlocks.map(block => {
              const weekFileRef = React.createRef();
              const isWeekPast = block.effectiveEnd.clone().endOf('day').isBefore(moment().tz(companyTimezone));
              const isWeekCurrent = !isWeekPast && block.effectiveStart.isSameOrBefore(moment().tz(companyTimezone), 'day');
              return (
                <Accordion key={block.key} defaultExpanded={isWeekCurrent} style={{ marginBottom: 16, background: "rgba(255, 255, 255, 0.03)", opacity: isWeekPast ? 0.85 : 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.weekHeader}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography style={{ fontWeight: 'bold', color: isWeekPast ? '#bbbbbb' : '#00BFA5' }}>
                          {block.label}
                        </Typography>
                        <Typography variant="caption" style={{ color: "#cccccc" }}>
                          {block.items.length === 1 ? i18n.t("statusScheduler.cardsCountSingular", {count: 1}) : i18n.t("statusScheduler.cardsCountPlural", {count: block.items.length})}
                        </Typography>
                        {isWeekPast && <Typography variant="caption" style={{ color: "#ff7043", fontWeight: 'bold' }}>• {i18n.t("statusScheduler.statusFinished")}</Typography>}
                        {isWeekCurrent && <Typography variant="caption" style={{ color: "#66bb6a", fontWeight: 'bold' }}>• {i18n.t("statusScheduler.statusOngoing")}</Typography>}
                      </Box>
                      <Box onClick={e => e.stopPropagation()}>
                        {!isWeekPast && (
                          <>
                            <Tooltip title={i18n.t("statusScheduler.importWeekTooltip")}>
                              <IconButton size="small" onClick={() => weekFileRef.current && weekFileRef.current.click()} style={{ color: "#26D0CE", marginRight: 4 }}>
                                <PublishIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <input type="file" ref={weekFileRef} style={{ display: 'none' }} accept=".json"
                              onChange={e => handleImportBulkFile(e, block.start.clone())} />
                          </>
                        )}
                        <Tooltip title={isWeekPast ? i18n.t("statusScheduler.noCardsToExport") : i18n.t("statusScheduler.exportWeekTooltip")}>
                          <span>
                            <IconButton size="small" onClick={() => handleExportWeek(block.items, block.label)} disabled={block.items.length === 0} style={{ color: block.items.length === 0 ? "#555" : "#26D0CE" }}>
                              <GetAppIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {block.items.length === 0 ? (
                      <Typography style={{ color: "#666", fontSize: "0.85rem", textAlign: 'center', width: '100%' }}>
                        {isWeekPast ? i18n.t("statusScheduler.emptyWeekPast") : i18n.t("statusScheduler.emptyWeekFuture")}
                      </Typography>
                    ) : (
                    <Grid container spacing={2}>
                      {block.items.map(s => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={s.id}>
                <Box
                  className={`${classes.scheduleCard} ${!s.isActive || s.status === 'SENT' ? classes.inactiveCard : ""}`}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center">
                      <MediaIcon type={s.mediaType} />
                      <Typography color="textPrimary" style={{ fontSize: "0.9rem", fontWeight: "bold", marginLeft: 8 }}>
                        {moment(s.scheduledAt).tz(companyTimezone).format("DD/MM/YYYY HH:mm")}
                      </Typography>
                    </Box>
                    <Box>
                      <Tooltip title="Importar JSON">
                        <span>
                          <IconButton size="small" onClick={() => fileInputRef.current.click()} style={{ color: "#26D0CE" }}>
                            <PublishIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Exportar JSON">
                        <span>
                          <IconButton size="small" onClick={() => handleExportCard(s)} style={{ color: "#26D0CE" }}>
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={i18n.t("statusScheduler.clone") || "Clonar"}>
                        <span>
                          <IconButton size="small" onClick={() => openClone(s)} color="inherit">
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {s.status === 'SENT' ? (
                        <Tooltip title={i18n.t("statusScheduler.view") || "Visualizar"}>
                          <span>
                            <IconButton size="small" onClick={() => setViewSchedule(s)} id={`btn-view-${s.id}`} style={{ color: "#1976d2" }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title={i18n.t("statusScheduler.edit") || "Editar"}>
                          <span>
                            <IconButton size="small" onClick={() => openEdit(s)} color="inherit">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title={i18n.t("statusScheduler.delete") || "Eliminar"}>
                        <IconButton size="small" onClick={() => handleDelete(s.id)} style={{ color: "#f44336" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Box>
                      {s.status === 'SENT' && (
                        <span className={classes.sentBadge}>{i18n.t("statusScheduler.sent") || "Enviado"}</span>
                      )}
                  </Box>

                  <Box display="flex" style={{ gap: 12, marginTop: 8 }}>
                    {s.mediaUrl && (
                      <Box 
                        flexShrink={0}
                        style={{ position: 'relative', cursor: 'pointer' }}
                        onClick={() => setPreviewMedia({ url: s.mediaUrl, type: s.mediaType, companyId: s.companyId })}
                      >
                        {(s.mediaType === "image" || (!s.mediaType && s.mediaUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(s.mediaUrl))) && (
                          <img
                            src={getMediaUrl(s.mediaUrl, s.companyId)}
                            alt="Status Preview"
                            style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4, border: "1px solid #ddd" }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        )}
                        {(s.mediaType === "video" || (!s.mediaType && s.mediaUrl && /\.(mp4|webm|ogg)$/i.test(s.mediaUrl))) && (
                          <video
                            src={`${getMediaUrl(s.mediaUrl, s.companyId)}#t=0.1`}
                            style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4, border: "1px solid #ddd", backgroundColor: "#000" }}
                            muted
                            preload="metadata"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        )}
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', borderRadius: 4 }}>
                          <ZoomInIcon style={{ color: 'white', fontSize: 32 }} />
                        </div>
                      </Box>
                    )}
                    
                    <Box overflow="hidden" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography color="textSecondary" style={{ fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center" }} title={s.groupIds?.map(id => availableGroups.find(g => id.includes(g.number))?.name || id.replace('@g.us', '')).join(", ")}>
                        <PeopleIcon fontSize="small" style={{ marginRight: 6, opacity: 0.7 }} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {s.groupIds?.map(id => availableGroups.find(g => id.includes(g.number))?.name || id.replace('@g.us', '')).join(", ")}
                        </span>
                      </Typography>
                      
                      {s.caption && (
                        <Typography color="textPrimary" style={{ fontSize: "0.85rem", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 }} title={s.caption}>
                          <AiPromptViewer prompt={s.caption} companyId={s.companyId} compact={true} />
                        </Typography>
                      )}

                      {s.mediaUrl && (
                        <Typography color="textSecondary" style={{ fontSize: "0.75rem", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          📎 {s.mediaUrl.split("/").pop()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {(() => {
                    const connName = s.whatsapp?.name || connections.find(c => c.id === s.whatsappId)?.name || (s.whatsappId === "all" ? i18n.t("statusScheduler.allConnections") : "");
                    return connName ? (
                      <Box mt={1} mb={1}>
                        <span style={{ fontSize: "0.75rem", background: "#eee", color: "#333", padding: "2px 6px", borderRadius: 4, fontWeight: "bold" }}>
                          📱 {connName}
                        </span>
                      </Box>
                    ) : null;
                  })()}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={s.isActive}
                        onChange={() => handleToggleActive(s)}
                        color="primary"
                        disabled={s.status === 'SENT'}
                      />
                    }
                    label={<Typography color="textSecondary" style={{ fontSize: "0.8rem" }}>{s.isActive ? (i18n.t("statusScheduler.active") || "Activo") : (i18n.t("statusScheduler.inactive") || "Inactivo")}</Typography>}
                    style={{ marginTop: 8 }}
                  />
                </Box>
              </Grid>
            ))}
                    </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
        </div>

        <AiContentFactory
          open={aiFactoryOpen}
          onClose={() => { setAiFactoryOpen(false); setAiEditSchedule(null); }}
          selectedWhatsapp={selectedWhatsapp}
          availableGroups={availableGroups}
          onScheduled={() => { loadSchedules(); setAiFactoryOpen(false); }}
          onRefreshGrid={() => loadSchedules()}
          initialQuantity={aiQuantity}
          editSchedule={aiEditSchedule}
          type="community"
          restrictedGroups={restrictedGroups}
          syncingGroups={syncingGroups}
          onSyncGroups={handleSyncGroups}
        />

                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".json" onChange={handleImportCard} />
        <input type="file" ref={bulkFileInputRef} style={{ display: 'none' }} accept=".json" onChange={handleImportBulkFile} />
        
        <Dialog 
          open={importBulkOpen} 
          onClose={() => !importingBulk && setImportBulkOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ className: classes.dialogPaper }}
        >
          <DialogTitle>Importar Tarjetas Semanales</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>Se encontraron <b>{importWeekSchedules.length}</b> tarjetas en el archivo.</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Nota: Las imÃ¡genes/videos no se importan. DeberÃ¡s editar cada tarjeta para volver a subirlos.
            </Typography>
            <Box mt={2}>
              <FormControl variant="outlined" size="small" fullWidth margin="dense">
                <InputLabel>ConexiÃ³n de WhatsApp destino</InputLabel>
                <Select
                  value={importBulkForm.whatsappId}
                  onChange={e => setImportBulkForm({ ...importBulkForm, whatsappId: e.target.value })}
                  label="ConexiÃ³n de WhatsApp destino"
                >
                  {connections.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
              
              <FormControl variant="outlined" margin="dense" fullWidth size="small" style={{ marginTop: 16 }}>
                <Autocomplete
                  multiple
                  size="small"
                  options={activeAdminCommunities}
                  getOptionLabel={(option) => option.name}
                  value={activeAdminCommunities.filter(g => importBulkForm.groupIds.includes(g.number))}
                  onChange={(e, v) => setImportBulkForm({ ...importBulkForm, groupIds: v.map(g => g.number) })}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Comunidades destino" placeholder="Agregar..." />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <div key={index} style={{
                        background: "#00BFA5", color: "white", borderRadius: 16, padding: "2px 8px", margin: 2, display: "flex", alignItems: "center", fontSize: "0.8rem"
                      }}>
                        {option.name}
                        <span onClick={() => {
                          const newIds = importBulkForm.groupIds.filter(id => id !== option.number);
                          setImportBulkForm({ ...importBulkForm, groupIds: newIds });
                        }} style={{ cursor: "pointer", marginLeft: 4, fontWeight: "bold" }}>Ã—</span>
                      </div>
                    ))
                  }
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={() => setImportBulkOpen(false)} className={classes.secondaryButton} disabled={importingBulk}>
              Cancelar
            </Button>
            <Button onClick={processBulkImport} disabled={importingBulk} className={classes.applyButton}>
              {importingBulk ? <CircularProgress size={20} style={{ color: "#FFF" }} /> : "Importar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal Agregar/Editar */}
        <Dialog 
          open={modalOpen} 
          onClose={() => setModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ className: classes.dialogPaper }}
        >
          <DialogTitle>
            {editingId ? (i18n.t("statusScheduler.editCommunityTitle") || "Editar Programación de Comunidad") : (i18n.t("statusScheduler.addCommunityTitle") || "Nueva Programación de Comunidad")}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              
              {/* Comunidades destino */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Autocomplete
                    multiple
                    size="small"
                    style={{ flexGrow: 1 }}
                    noOptionsText={i18n.t("statusScheduler.noOptions") || "Sin resultados"}
                    options={activeAdminCommunities}
                    getOptionLabel={(option) => option.name}
                    value={activeAdminCommunities.filter(g => form.groupIds.includes(`${g.number}@g.us`))}
                    onChange={(event, newValue) => {
                      setForm(f => ({ ...f, groupIds: newValue.map(v => `${v.number}@g.us`) }));
                    }}
                    disableCloseOnSelect
                    renderOption={(option, { selected }) => (
                      <React.Fragment>
                        <Checkbox
                          color="primary"
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.name}
                      </React.Fragment>
                    )}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        variant="outlined" 
                        label={i18n.t("statusScheduler.targetCommunities") || "Comunidades destino"} 
                        placeholder={i18n.t("statusScheduler.searchCommunities") || "Buscar comunidades..."} 
                      />
                    )}
                  />
                  <Tooltip title={i18n.t("statusScheduler.syncTooltip")}>
                    <Box ml={1}>
                      <IconButton 
                        color="primary" 
                        onClick={handleSyncGroups} 
                        disabled={syncingGroups || !selectedWhatsapp}
                        style={{ border: '1px solid rgba(0,0,0,0.1)', padding: 8 }}
                      >
                        {syncingGroups ? <CircularProgress size={24} /> : <SyncIcon />}
                      </IconButton>
                    </Box>
                  </Tooltip>
                </Box>
              </Grid>

              {/* Día y Hora (Datetime) */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  type="datetime-local"
                  label={i18n.t("statusScheduler.time") || "Fecha y Hora"}
                  value={form.scheduledAt}
                  onChange={e => { const val = e.target.value; setForm(f => ({ ...f, scheduledAt: val })); }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Tipo de medio */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel>{i18n.t("statusScheduler.mediaType")}</InputLabel>
                  <Select
                    value={form.mediaType}
                    onChange={e => { const val = e.target.value; setForm(f => ({ ...f, mediaType: val, mediaUrl: "", caption: "" })); }}
                    label={i18n.t("statusScheduler.mediaType")}
                  >
                    <MenuItem value="text">📝 {i18n.t("statusScheduler.typeText")}</MenuItem>
                    <MenuItem value="image">🖼️ {i18n.t("statusScheduler.typeImage")}</MenuItem>
                    <MenuItem value="video">🎬 {i18n.t("statusScheduler.typeVideo")}</MenuItem>
                    <MenuItem value="document">📄 {i18n.t("statusScheduler.typeDocument") || "Documento"}</MenuItem>
                    <MenuItem value="audio">🎵 {i18n.t("statusScheduler.typeAudio") || "Audio"}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Texto */}
              {form.mediaType === "text" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" variant="outlined" multiline minRows={3} maxRows={10}
                    label={i18n.t("statusScheduler.caption") || "Mensaje de texto"}
                    value={form.caption}
                    onChange={e => { const val = e.target.value; setForm(f => ({ ...f, caption: val })); }}
                  />
                  <Typography style={{ fontSize: "0.85rem", fontWeight: "bold", color: "#ccff00", textShadow: "0px 0px 8px rgba(204,255,0,0.8)", marginTop: 8 }}>
                    {i18n.t("statusScheduler.markdownTip")}
                  </Typography>
                </Grid>
              )}

              {/* Imagen, Video, Documento o Audio: selector de Archivos */}
              {(form.mediaType === "image" || form.mediaType === "video" || form.mediaType === "document" || form.mediaType === "audio") && (
                <>
                  <Grid item xs={12}>
                    <Box className={classes.fileInputRow}>
                      <TextField
                        fullWidth size="small" variant="outlined"
                        label={i18n.t("statusScheduler.fileUrl")}
                        placeholder="/files/mi-imagen.png"
                        value={form.mediaUrl}
                        onChange={e => { const val = e.target.value; setForm(f => ({ ...f, mediaUrl: val })); }}
                      />
                      <Tooltip title={i18n.t("statusScheduler.openFileManager")}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FolderOpenIcon />}
                          onClick={() => setSelectorModalOpen(true)}
                          style={{ whiteSpace: "nowrap", padding: "0 20px" }}
                        >
                          {i18n.t("statusScheduler.files")}
                        </Button>
                      </Tooltip>
                    </Box>
                    <Typography style={{ fontSize: "0.75rem", color: "#888", marginTop: 4 }}>
                      {i18n.t("statusScheduler.fileHint")}
                    </Typography>
                    {form.mediaUrl && (form.mediaType === "image" || form.mediaType === "video") && (
                      <Box mt={2} display="flex" justifyContent="center">
                        <img 
                          src={form.mediaUrl.startsWith("http") ? form.mediaUrl : `${process.env.REACT_APP_BACKEND_URL}/public/${form.mediaUrl.replace(/^\/?(public\/)?/, "")}`} 
                          alt="Preview" 
                          style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, objectFit: "contain", border: "1px solid #ddd" }}
                          onError={(e) => {
                            if (e.target.dataset.tried) {
                                e.target.style.display = "none";
                                return;
                            }
                            e.target.dataset.tried = "true";
                            e.target.src = `${process.env.REACT_APP_BACKEND_URL}/public/company1/files/${form.mediaUrl.split("/").pop()}`;
                          }}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small" variant="outlined" multiline minRows={2} maxRows={8}
                      label={i18n.t("statusScheduler.captionOptional") || "Leyenda (Opcional)"}
                      value={form.caption}
                      onChange={e => { const val = e.target.value; setForm(f => ({ ...f, caption: val })); }}
                    />
                    <Typography style={{ fontSize: "0.75rem", color: "#00e5ff", textShadow: "0px 0px 4px rgba(0,229,255,0.6)", marginTop: 4 }}>
                      {i18n.t("statusScheduler.markdownTip")}
                    </Typography>
                  </Grid>
                </>
              )}

              {/* Mention All & Interval */}
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.mentionAll}
                      onChange={e => { const checked = e.target.checked; setForm(f => ({ ...f, mentionAll: checked })); }}
                      color="primary"
                    />
                  }
                  label={i18n.t("statusScheduler.mentionAll") || "Mencionar a todos (@todos)"}
                />
                <Typography variant="caption" style={{ display: 'block', marginTop: 4, lineHeight: 1.2, color: '#ff8a80', fontWeight: 'bold' }}>
                  {i18n.t("statusScheduler.mentionSpamWarning")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  type="number"
                  label={i18n.t("statusScheduler.sendInterval") || "Pausa entre envíos (segs)"}
                  value={form.sendInterval}
                  onChange={e => { 
                    let val = parseInt(e.target.value, 10);
                    if (isNaN(val)) val = 15;
                    if (val < 15) val = 15;
                    if (val > 120) val = 120;
                    setForm(f => ({ ...f, sendInterval: val })); 
                  }}
                  inputProps={{ min: 15, max: 120 }}
                />
              </Grid>

              {/* Activo */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={e => { const checked = e.target.checked; setForm(f => ({ ...f, isActive: checked })); }}
                      color="primary"
                    />
                  }
                  label={i18n.t("statusScheduler.isActive")}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={() => setModalOpen(false)} className={classes.secondaryButton}>
              {i18n.t("statusScheduler.cancel") || "Cancelar"}
            </Button>
            {form.status !== 'SENT' && (
              <Button onClick={handleSave} className={classes.applyButton} disabled={loading}>
                {loading ? <CircularProgress size={20} style={{ color: "#FFF" }} /> : (i18n.t("statusScheduler.save") || "Guardar")}
              </Button>
            )}
          </DialogActions>
        </Dialog>
        
        <Dialog open={Boolean(previewMedia)} onClose={() => setPreviewMedia(null)} maxWidth="md" fullWidth>
          <Box display="flex" justifyContent="center" alignItems="center" style={{ background: "#000", minHeight: 300, position: 'relative' }}>
            {previewMedia && previewMedia.type === "video" ? (
              <video 
                src={`${getMediaUrl(previewMedia.url, previewMedia.companyId)}#t=0.1`}
                controls 
                autoPlay
                style={{ width: "100%", maxHeight: "85vh", objectFit: "contain" }} 
              />
            ) : previewMedia && (
              <img 
                src={getMediaUrl(previewMedia.url, previewMedia.companyId)}
                alt="Preview" 
                style={{ width: "100%", maxHeight: "85vh", objectFit: "contain" }} 
              />
            )}
          </Box>
        </Dialog>

        <Dialog 
          open={Boolean(viewSchedule)} 
          onClose={() => setViewSchedule(null)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ className: classes.dialogPaper }}
        >
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon style={{ color: '#1976d2', marginRight: 8 }} />
            Ver contenido enviado
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {viewSchedule && (
            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              {viewSchedule.mediaUrl && (viewSchedule.mediaType === 'image' || (!viewSchedule.mediaType && /\.(jpg|jpeg|png|gif|webp)$/i.test(viewSchedule.mediaUrl))) && (
                <Box textAlign="center">
                  <img
                      src={getMediaUrl(viewSchedule.mediaUrl, viewSchedule.companyId)}
                    alt="Contenido"
                    style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 8, border: '1px solid #ddd' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </Box>
              )}
              {viewSchedule.caption && (
                <Box>
                  <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalTextCaption")}</Typography>
                  <Box
                    style={{
                      marginTop: 6,
                      background: 'rgba(128, 128, 128, 0.1)',
                      borderRadius: 6,
                      padding: '10px 14px',
                      fontFamily: 'monospace',
                      fontSize: '0.92rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    <AiPromptViewer prompt={viewSchedule.caption} companyId={viewSchedule.companyId} />
                  </Box>
                </Box>
              )}
              {viewSchedule.aiGeneration && viewSchedule.aiPrompt && (
                <Box>
                  <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalAiPrompt")}</Typography>
                  <Box
                    style={{
                      marginTop: 6,
                      background: 'rgba(156, 39, 176, 0.1)',
                      borderLeft: '4px solid #9c27b0',
                      borderRadius: '0 6px 6px 0',
                      padding: '10px 14px',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    <AiPromptViewer prompt={viewSchedule.aiPrompt} companyId={viewSchedule.companyId} />
                  </Box>
                </Box>
              )}
              <Box display="flex" style={{ gap: 24, flexWrap: 'wrap' }}>
                {viewSchedule.scheduledAt && (
                  <Box>
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalDateTime")}</Typography>
                    <Typography>{viewSchedule.scheduledAt}</Typography>
                  </Box>
                )}
                {viewSchedule.mediaUrl && (
                  <Box>
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalFile")}</Typography>
                    <Typography style={{ fontSize: '0.82rem', wordBreak: 'break-all' }}>📎 {viewSchedule.mediaUrl.split("/").pop()}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          {viewSchedule?.caption && (
            <Button 
              onClick={() => { navigator.clipboard.writeText(viewSchedule.caption); }} 
              className={classes.applyButton}
              style={{ marginRight: 'auto' }}
            >
              Copiar texto
            </Button>
          )}
          <Button 
            onClick={() => setViewSchedule(null)} 
            className={classes.secondaryButton}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      <AiVideoModal open={aiVideoOpen} onClose={() => setAiVideoOpen(false)} onVideoGenerated={handleVideoGenerated} />

      <FileManagerSelectorModal
        open={selectorModalOpen}
          onClose={() => setSelectorModalOpen(false)}
          onSelect={(path) => setForm(f => ({ ...f, mediaUrl: path }))}
          filterType={form.mediaType}
        />

        <Snackbar
          open={snack.open}
          autoHideDuration={6000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert severity={snack.severity}>{snack.message}</Alert>
        </Snackbar>
      </Paper>
    </MainContainer>
  );
}
